import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { UserPlus, UsersRound, Download, Search as SearchIcon, X, UserCheck2, UserX2, Undo2, UserRoundPlus, ArrowUp, ArrowDown, TrendingUp, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { useEntityMaps, resolveAgencyLabel } from '@/hooks/useEntityMaps';
import BulkInviteDialog from './BulkInviteDialog';
import InviteAllEligibleDialog from './InviteAllEligibleDialog';
import InlineRegisterAgent from './InlineRegisterAgent';
import ParticipantDetailDrawer from './ParticipantDetailDrawer';
import WalkInDialog from './WalkInDialog';
import ProspectDrawer from './ProspectDrawer';

const INV_TONE = {
  not_invited: 'bg-slate-100 text-slate-600',
  invited: 'bg-sky-100 text-sky-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-rose-100 text-rose-800',
};
const REG_TONE = {
  not_registered: 'bg-slate-100 text-slate-600',
  registered: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-200 text-slate-500 line-through',
  waitlisted: 'bg-purple-100 text-purple-800',
};
const RSVP_TONE = {
  going: 'bg-emerald-100 text-emerald-700',
  maybe: 'bg-amber-100 text-amber-800',
  declined: 'bg-slate-200 text-slate-700',
};
const ATT_TONE = {
  pending: 'bg-slate-100 text-slate-600',
  attended: 'bg-sky-100 text-sky-800',
  no_show: 'bg-amber-100 text-amber-800',
  walk_in: 'bg-violet-100 text-violet-800',
};

const SOURCE_LABEL = {
  portal: 'Portal',
  public: 'Public Website',
  public_website: 'Public Website',
  admin: 'Admin',
  organizer: 'Organizer',
  audience_resolution: 'Auto (Audience)',
  import: 'Import',
  api: 'API',
};

const AUTO_INVITE_SCOPES = new Set(['agency', 'specific_users']);

const asDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');

export default function ParticipantsPanel({
  event, participants, currentUser, canManage, onChanged,
}) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkOpenPreRegistered, setBulkOpenPreRegistered] = useState(false);
  const [allEligibleOpen, setAllEligibleOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [prospectAssignmentId, setProspectAssignmentId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({
    invitation_status: '', registration_status: '', rsvp_response: '',
    attendance_status: '', agency: '', registration_source: '',
    only_prospects: false,
  });

  // Phase 27.35 — Prospect visibility. Rows registered via the share
  // link OR the public event page with an unrecognised NPN come back
  // with `is_prospect=true`. We surface them with a subtle amber
  // tint + inline badge and a one-click "Only Prospects" filter so
  // recruiters can drill into new leads instantly.
  const prospectCount = useMemo(
    () => (participants || []).filter((p) => p.is_prospect).length,
    [participants],
  );

  const { users, agencies } = useEntityMaps({
    currentUser, canFetchAll: canManage, enabled: true,
  });

  const canInviteAll =
    canManage && event && AUTO_INVITE_SCOPES.has(event.visibility_scope)
    && !['archived', 'cancelled', 'completed'].includes(event.status);
  const canInviteBulk =
    canManage && event
    && !['archived', 'cancelled', 'completed'].includes(event.status);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v === '__all__' ? '' : v }));
  const clearFilters = () => {
    setFilters({ invitation_status: '', registration_status: '', rsvp_response: '',
                 attendance_status: '', agency: '', registration_source: '',
                 only_prospects: false });
    setQ('');
  };

  const agencyOptions = useMemo(() => {
    const set = new Set();
    (participants || []).forEach((p) => {
      const agencyId = users?.[p.user_id]?.agency_id;
      if (agencyId && agencies?.[agencyId]?.name) set.add(agencies[agencyId].name);
      if (p.guest_agency) set.add(p.guest_agency);
    });
    return Array.from(set).sort();
  }, [participants, users, agencies]);

  const filtered = useMemo(() => {
    const rows = participants || [];
    const search = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.only_prospects && !r.is_prospect) return false;
      if (filters.invitation_status && r.invitation_status !== filters.invitation_status) return false;
      if (filters.registration_status && r.registration_status !== filters.registration_status) return false;
      if (filters.rsvp_response && (r.rsvp_response || '') !== filters.rsvp_response) return false;
      if (filters.attendance_status && r.attendance_status !== filters.attendance_status) return false;
      if (filters.registration_source && r.registration_source !== filters.registration_source) return false;
      if (filters.agency) {
        const u = users?.[r.user_id];
        const ag = u?.agency_id ? agencies?.[u.agency_id]?.name : r.guest_agency;
        if (ag !== filters.agency) return false;
      }
      if (search) {
        const u = users?.[r.user_id];
        // Coerce every field to a string first — NPN and phone can be
        // numeric in Mongo and `.toLowerCase()` on a Number throws,
        // which was propagating up to the app-level ErrorBoundary and
        // showing "Something went wrong" as soon as the user typed.
        const s = (v) => (v == null ? '' : String(v)).toLowerCase();
        const name = s(u?.name || u?.email || `${r.guest_first_name || ''} ${r.guest_last_name || ''}`);
        const email = s(u?.email || r.guest_email);
        const phone = s(u?.phone || r.guest_phone);
        const npn = s(r.guest_npn || u?.agent_npn);
        const ag = s(u?.agency_id ? agencies?.[u.agency_id]?.name : r.guest_agency);
        if (![name, email, phone, npn, ag].some((f) => f.includes(search))) return false;
      }
      return true;
    });
  }, [participants, users, agencies, filters, q]);

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => k === 'only_prospects' ? v : Boolean(v)).length + (q ? 1 : 0);

  const exportCsv = () => {
    const url = calendarAPI.participantsCsvUrl(event.event_id, {
      ...(filters.invitation_status && { invitation_status: filters.invitation_status }),
      ...(filters.registration_status && { registration_status: filters.registration_status }),
      ...(filters.rsvp_response && { rsvp_response: filters.rsvp_response }),
      ...(filters.attendance_status && { attendance_status: filters.attendance_status }),
      ...(filters.registration_source && { registration_source: filters.registration_source }),
    });
    // Use axios to include the auth header, then trigger download.
    import('@/lib/api').then(({ default: api }) => {
      api.get(url.replace(api.defaults.baseURL, ''), { responseType: 'blob' })
        .then((r) => {
          const blob = new Blob([r.data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `participants-${event.event_id.slice(0, 8)}.csv`;
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch((e) => toast.error(getErrorMessage(e, 'Export failed')));
    });
  };

  const me = (participants || []).find((p) => p.user_id === currentUser?.user_id);

  // Phase 26.3 — Attendance quick actions.
  const doAttendance = async (aid, action) => {
    setBusy(true);
    try {
      if (action === 'check_in') await calendarAPI.checkIn(event.event_id, aid);
      else if (action === 'undo_check_in') await calendarAPI.undoCheckIn(event.event_id, aid);
      else if (action === 'no_show') await calendarAPI.markNoShow(event.event_id, aid);
      toast.success(
        action === 'check_in' ? 'Checked in'
        : action === 'undo_check_in' ? 'Check-in undone'
        : 'Marked no-show'
      );
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Attendance action failed'));
    } finally {
      setBusy(false);
    }
  };

  const doBulk = async (action) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await calendarAPI.bulkAttendance(event.event_id, action, Array.from(selected));
      const label = action === 'check_in' ? 'checked in'
        : action === 'undo_check_in' ? 'reverted'
        : 'marked no-show';
      toast.success(`${data.count} participant(s) ${label}${data.errors?.length ? `, ${data.errors.length} skipped` : ''}`);
      setSelected(new Set());
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Bulk action failed'));
    } finally {
      setBusy(false);
    }
  };

  const toggleSelected = (aid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(aid)) next.delete(aid); else next.add(aid);
      return next;
    });
  };
  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.assignment_id));
  const toggleSelectAll = () => {
    if (allFilteredSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.assignment_id)));
  };

  // Phase 26.4 — Waitlist actions.
  const doWaitlist = async (aid, action, direction) => {
    setBusy(true);
    try {
      if (action === 'promote') await calendarAPI.waitlistPromote(event.event_id, aid);
      else if (action === 'remove') await calendarAPI.waitlistRemove(event.event_id, aid);
      else if (action === 'move') await calendarAPI.waitlistMove(event.event_id, aid, direction);
      toast.success(
        action === 'promote' ? 'Promoted from waitlist'
        : action === 'remove' ? 'Removed from waitlist'
        : `Moved ${direction}`
      );
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Waitlist action failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="participants-panel">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <UsersRound size={14} className="text-slate-500" />
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Participants</h4>
        <span className="text-xs text-slate-500" data-testid="participants-count">
          {filtered.length}{filtered.length !== (participants?.length || 0) ? ` of ${participants?.length || 0}` : ''}
          {event?.capacity ? ` · cap ${event.capacity}` : ''}
        </span>
        {prospectCount > 0 && (
          <button
            type="button"
            onClick={() => setFilters((f) => ({ ...f, only_prospects: !f.only_prospects }))}
            className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border transition ${
              filters.only_prospects
                ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
            }`}
            title={filters.only_prospects ? 'Showing only prospects — click to reset' : 'Show only prospects — non-KR NPNs captured for recruiting'}
            data-testid="participants-prospects-chip"
          >
            <Sparkles size={11} /> {prospectCount} Prospect{prospectCount === 1 ? '' : 's'}
          </button>
        )}
        {canManage && (
          <div className="ml-auto flex flex-wrap gap-1">
            {(participants || []).length > 0 && (
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="participants-export-csv-btn">
                <Download size={13} className="mr-1" /> Export CSV
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setWalkInOpen(true)} data-testid="participants-walk-in-btn">
              <UserRoundPlus size={13} className="mr-1" /> Walk-In
            </Button>
            {canInviteBulk && (
              <InlineRegisterAgent
                eventId={event?.event_id}
                onDone={onChanged}
              />
            )}
            {canInviteBulk && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkOpenPreRegistered(true)}
                data-testid="participants-register-agent-btn"
                title="Register a batch of agents (opens the full dialog)"
              >
                <UserCheck2 size={13} className="mr-1" /> Batch Register
              </Button>
            )}
            {canInviteAll && (
              <Button size="sm" variant="outline" onClick={() => setAllEligibleOpen(true)} data-testid="participants-invite-all-btn">
                Invite All Eligible
              </Button>
            )}
            {canInviteBulk && (
              <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="participants-bulk-invite-btn">
                <UserPlus size={14} className="mr-1" /> Bulk Invite
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Own-row RSVP shortcut for non-manager principals */}
      {!canManage && me && (
        <div className="mb-2 p-2 rounded-md bg-slate-50 border border-slate-200 text-xs" data-testid="participants-my-row">
          You are: <Badge className={`ml-1 ${INV_TONE[me.invitation_status] || ''}`}>{me.invitation_status || me.status}</Badge>
        </div>
      )}

      {canManage && selected.size > 0 && (
        <div className="mb-2 p-2 rounded-md bg-sky-50 border border-sky-200 flex items-center gap-2 flex-wrap" data-testid="participants-bulk-bar">
          <span className="text-xs font-medium text-sky-900" data-testid="participants-bulk-count">
            {selected.size} selected
          </span>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => doBulk('check_in')} data-testid="bulk-check-in-btn">
            <UserCheck2 size={13} className="mr-1" /> Check In
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => doBulk('no_show')} data-testid="bulk-no-show-btn">
            <UserX2 size={13} className="mr-1" /> Mark No-Show
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => doBulk('undo_check_in')} data-testid="bulk-undo-btn">
            <Undo2 size={13} className="mr-1" /> Undo
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="ml-auto" data-testid="bulk-clear-btn">
            Clear
          </Button>
        </div>
      )}

      {canManage && (participants || []).length > 0 && (
        <div className="mb-2 space-y-2" data-testid="participants-filters">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-[180px] max-w-md">
              <SearchIcon size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, agency, NPN…" className="pl-8 h-8 text-sm" data-testid="participants-search" />
            </div>
            {activeFilterCount > 0 && (
              <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="participants-clear-filters">
                <X size={12} className="mr-1" /> Clear ({activeFilterCount})
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterSelect testId="filter-invitation" label="Invitation" value={filters.invitation_status} options={['not_invited', 'invited', 'accepted', 'declined']} onChange={(v) => setFilter('invitation_status', v)} />
            <FilterSelect testId="filter-registration" label="Registration" value={filters.registration_status} options={['not_registered', 'registered', 'waitlisted', 'cancelled']} onChange={(v) => setFilter('registration_status', v)} />
            <FilterSelect testId="filter-rsvp" label="RSVP" value={filters.rsvp_response} options={['going', 'maybe', 'declined']} onChange={(v) => setFilter('rsvp_response', v)} />
            <FilterSelect testId="filter-attendance" label="Attendance" value={filters.attendance_status} options={['pending', 'attended', 'no_show', 'walk_in']} onChange={(v) => setFilter('attendance_status', v)} />
            <FilterSelect testId="filter-source" label="Source" value={filters.registration_source} options={['portal', 'public', 'admin', 'organizer', 'audience_resolution']} onChange={(v) => setFilter('registration_source', v)} />
            {agencyOptions.length > 0 && (
              <FilterSelect testId="filter-agency" label="Agency" value={filters.agency} options={agencyOptions} onChange={(v) => setFilter('agency', v)} rawLabels />
            )}
          </div>
        </div>
      )}

      {(participants || []).length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="participants-empty">
          {canManage
            ? 'No participants yet. Use Bulk Invite or Invite All Eligible to populate this event.'
            : 'No participant records visible to you.'}
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-slate-500 py-3" data-testid="participants-filtered-empty">
          No participants match the current filters.
        </p>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded" data-testid="participants-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                {canManage && (
                  <th className="px-2 py-1.5 w-8">
                    <Checkbox
                      checked={allFilteredSelected}
                      onCheckedChange={toggleSelectAll}
                      data-testid="participants-select-all"
                      aria-label="Select all filtered participants"
                    />
                  </th>
                )}
                <th className="px-2 py-1.5">Participant</th>
                <th className="px-2 py-1.5">Email</th>
                <th className="px-2 py-1.5">Agency</th>
                <th className="px-2 py-1.5">Invitation</th>
                <th className="px-2 py-1.5">Registration</th>
                <th className="px-2 py-1.5">RSVP</th>
                <th className="px-2 py-1.5">Attendance</th>
                <th className="px-2 py-1.5">Guests</th>
                <th className="px-2 py-1.5">Source</th>
                <th className="px-2 py-1.5">Registered At</th>
                <th className="px-2 py-1.5">Checked In At</th>
                <th className="px-2 py-1.5">Checked In By</th>
                {canManage && <th className="px-2 py-1.5 w-32">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => {
                const u = users?.[p.user_id];
                const displayName = u?.name || u?.email || [p.guest_first_name, p.guest_last_name].filter(Boolean).join(' ') || '—';
                const email = u?.email || p.guest_email || '—';
                const agency = u?.agency_id ? (agencies?.[u.agency_id]?.name || resolveAgencyLabel(agencies, users, p.user_id, currentUser)) : (p.guest_agency || '—');
                const isAttended = p.attendance_status === 'attended';
                const isWalkIn = p.attendance_status === 'walk_in';
                const isNoShow = p.attendance_status === 'no_show';
                const isCancelled = p.registration_status === 'cancelled';
                const isProspect = !!p.is_prospect;
                return (
                  <tr key={p.assignment_id}
                      className={`hover:bg-slate-50 ${isProspect ? 'bg-amber-50/50 hover:bg-amber-50' : ''}`}
                      data-testid={`participant-row-${p.assignment_id}`}
                      data-prospect={isProspect ? 'true' : undefined}>
                    {canManage && (
                      <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.has(p.assignment_id)}
                          onCheckedChange={() => toggleSelected(p.assignment_id)}
                          data-testid={`participant-select-${p.assignment_id}`}
                          aria-label={`Select ${displayName}`}
                        />
                      </td>
                    )}
                    <td className="px-2 py-1.5 truncate max-w-[220px] font-medium text-slate-800 cursor-pointer" onClick={() => (isProspect ? setProspectAssignmentId(p.assignment_id) : setDetailRow(p))}>
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{displayName}</span>
                        {isProspect && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setProspectAssignmentId(p.assignment_id); }}
                            className="focus:outline-none focus:ring-2 focus:ring-amber-400 rounded"
                            data-testid={`prospect-badge-${p.assignment_id}`}
                            title="Open recruiting drawer for this prospect"
                          >
                            <Badge
                              className="bg-amber-100 text-amber-900 border border-amber-200 text-[9px] font-semibold uppercase tracking-wide flex-shrink-0 hover:bg-amber-200 cursor-pointer"
                            >
                              Prospect
                            </Badge>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 truncate max-w-[180px] cursor-pointer" onClick={() => (isProspect ? setProspectAssignmentId(p.assignment_id) : setDetailRow(p))}>{email}</td>
                    <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]">{agency}</td>
                    <td className="px-2 py-1.5"><Badge className={`text-[10px] capitalize ${INV_TONE[p.invitation_status] || ''}`}>{(p.invitation_status || '—').replace(/_/g, ' ')}</Badge></td>
                    <td className="px-2 py-1.5"><Badge className={`text-[10px] capitalize ${REG_TONE[p.registration_status] || ''}`}>{(p.registration_status || '—').replace(/_/g, ' ')}</Badge></td>
                    <td className="px-2 py-1.5">{p.rsvp_response ? <Badge className={`text-[10px] capitalize ${RSVP_TONE[p.rsvp_response] || ''}`}>{p.rsvp_response}</Badge> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-1.5">{p.attendance_status ? <Badge className={`text-[10px] capitalize ${ATT_TONE[p.attendance_status] || ''}`} data-testid={`attendance-badge-${p.assignment_id}`}>{p.attendance_status.replace(/_/g, ' ')}</Badge> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-1.5 text-slate-700 tabular-nums">{p.guest_count || 0}</td>
                    <td className="px-2 py-1.5 text-slate-500 text-[11px]">{SOURCE_LABEL[p.registration_source] || (p.registration_source ? p.registration_source.replace(/_/g, ' ') : <span className="text-slate-400">Unknown</span>)}</td>
                    <td className="px-2 py-1.5 text-slate-500">{asDate(p.registered_at || p.assigned_at)}</td>
                    <td className="px-2 py-1.5 text-slate-500 text-[11px]" data-testid={`checked-in-at-${p.assignment_id}`}>
                      {p.checked_in_at ? new Date(p.checked_in_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-slate-500 truncate max-w-[140px]" data-testid={`checked-in-by-${p.assignment_id}`}>
                      {p.checked_in_by_name || <span className="text-slate-300">—</span>}
                    </td>
                    {canManage && (
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          {p.registration_status === 'waitlisted' ? (
                            <>
                              {p.waitlist_position != null && (
                                <span className="mr-1 inline-flex items-center rounded bg-purple-100 text-purple-800 text-[10px] font-semibold px-1.5 py-0.5" data-testid={`waitlist-pos-${p.assignment_id}`}>#{p.waitlist_position}</span>
                              )}
                              <button type="button" className="p-1 rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-40" disabled={busy} title="Promote" onClick={() => doWaitlist(p.assignment_id, 'promote')} data-testid={`wl-promote-${p.assignment_id}`}>
                                <TrendingUp size={13} />
                              </button>
                              <button type="button" className="p-1 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-40" disabled={busy} title="Move up" onClick={() => doWaitlist(p.assignment_id, 'move', 'up')} data-testid={`wl-up-${p.assignment_id}`}>
                                <ArrowUp size={13} />
                              </button>
                              <button type="button" className="p-1 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-40" disabled={busy} title="Move down" onClick={() => doWaitlist(p.assignment_id, 'move', 'down')} data-testid={`wl-down-${p.assignment_id}`}>
                                <ArrowDown size={13} />
                              </button>
                              <button type="button" className="p-1 rounded hover:bg-rose-50 text-rose-700 disabled:opacity-40" disabled={busy} title="Remove from waitlist" onClick={() => doWaitlist(p.assignment_id, 'remove')} data-testid={`wl-remove-${p.assignment_id}`}>
                                <X size={13} />
                              </button>
                            </>
                          ) : isAttended || isWalkIn ? (
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-40"
                              disabled={busy || isCancelled}
                              title="Undo check-in"
                              onClick={() => doAttendance(p.assignment_id, 'undo_check_in')}
                              data-testid={`row-undo-${p.assignment_id}`}
                            >
                              <Undo2 size={13} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-40"
                              disabled={busy || isCancelled || p.registration_status !== 'registered'}
                              title={isCancelled ? 'Cancelled' : p.registration_status !== 'registered' ? 'Not registered yet' : 'Check in'}
                              onClick={() => doAttendance(p.assignment_id, 'check_in')}
                              data-testid={`row-checkin-${p.assignment_id}`}
                            >
                              <UserCheck2 size={13} />
                            </button>
                          )}
                          {p.registration_status !== 'waitlisted' && (
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-amber-50 text-amber-700 disabled:opacity-40"
                              disabled={busy || isNoShow || isCancelled}
                              title="Mark no-show"
                              onClick={() => doAttendance(p.assignment_id, 'no_show')}
                              data-testid={`row-noshow-${p.assignment_id}`}
                            >
                              <UserX2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BulkInviteDialog open={bulkOpen} event={event} onClose={() => setBulkOpen(false)} onDone={onChanged} />
      <BulkInviteDialog
        open={bulkOpenPreRegistered}
        event={event}
        onClose={() => setBulkOpenPreRegistered(false)}
        onDone={onChanged}
        defaultRegisterDirectly
      />
      <InviteAllEligibleDialog open={allEligibleOpen} event={event} onClose={() => setAllEligibleOpen(false)} onDone={onChanged} />
      <WalkInDialog open={walkInOpen} eventId={event?.event_id} onClose={() => setWalkInOpen(false)} onDone={onChanged} />
      <ParticipantDetailDrawer
        open={!!detailRow}
        event={event}
        row={detailRow}
        users={users}
        currentUser={currentUser}
        canManage={canManage}
        onClose={() => setDetailRow(null)}
        onChanged={onChanged}
      />
      <ProspectDrawer
        open={!!prospectAssignmentId}
        onOpenChange={(v) => { if (!v) setProspectAssignmentId(null); }}
        assignmentId={prospectAssignmentId}
        event={event}
      />
    </section>
  );
}

const FilterSelect = ({ label, value, options, onChange, testId, rawLabels = false }) => (
  <Select value={value || '__all__'} onValueChange={onChange}>
    <SelectTrigger className="h-8 text-xs w-auto min-w-[130px]" data-testid={testId}>
      <SelectValue placeholder={label} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="__all__">{`All ${label}`}</SelectItem>
      {options.map((o) => (
        <SelectItem key={o} value={o} className="capitalize">
          {rawLabels ? o : o.replace(/_/g, ' ')}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);
